/* eslint-disable multiline-ternary */
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from '@mui/material/Skeleton';
import {
    createUpdatedUser,
    sortOptons,
    translate,
    AlertHandler,
    stripPrefix,
} from '../../../util/misc';
import { FooterLink, SectionTitle } from '../../../components/atoms/Profile';

import {
    Box,
    Typography,
    MenuItem,
    Select,
    useSnackbar,
    Grid,
} from '@bluedolphin/ui'; // skeleton from bluedolpine is breaking test
import { AppInsights, Head } from '@bluedolphin/util-module-mf';
import OutlinedInput from '@mui/material/OutlinedInput';

import { SelectChangeEvent } from '@mui/material/Select';

import { AvatarInitials } from '../../../components/atoms/AvatarInitials';
import CustomAutocomplete from '../../../components/atoms/CustomAutocomplete';
import { FormField } from '../../../components/atoms/FormField';
import { useUser } from '../../../providers/user';
import { responsibilities, departments, mode } from './options';
const { useAppInsights } = AppInsights;

type ChangeValue = { value: string } | { value: string }[] | boolean;
type Option = {
    value: string;
    label: string;
};

export const debounce = <F extends (...args: any[]) => any>(
    func: F,
    waitFor = 500
) => {
    let timeout: NodeJS.Timeout;
    return (...args: Parameters<F>): Promise<ReturnType<F>> =>
        new Promise((resolve) => {
            if (timeout) {
                clearTimeout(timeout);
            }
            timeout = setTimeout(() => resolve(func(...args)), waitFor);
        });
};

const Profile = () => {
    const { user, updateUser, userRoles } = useUser();
    const { enqueueSnackbar } = useSnackbar();
    const appInsights = useAppInsights();
    const { t, i18n } = useTranslation();
    const [userData, setUserData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [selected, setSelected] = useState(mode[0]?.value);
    const [responsibility, setResponsibility] = useState<Option[] | null>(null);
    const [workFunction, setWorkFunction] = useState<Option[] | null>(null);
    const [department, setDepartment] = useState<Option[] | null>(null);

    const [changes, setChanges] = useState<{ [key: string]: ChangeValue }>({});

    const handleSelectChange = (event: SelectChangeEvent<string>) => {
        setSelected(event.target.value);
        setChanges((prevChanges) => ({
            ...prevChanges,
            default_view_mode: event.target.value === 'view',
        }));
    };

    const handleAutocompleteOnchange = (
        selectedItem: { value: string } | { value: string }[],
        id: string
    ) => {
        setChanges((prevChanges) => ({
            ...prevChanges,
            [id]: selectedItem,
        }));
    };

    const updateUserWithDebounce = useCallback(
        // eslint-disable-next-line space-before-function-paren
        debounce(async (changes, userData, userId) => {
            if (Object.keys(changes).length) {
                const updatedUserObj = createUpdatedUser(userData, changes);
                const response = await updateUser(updatedUserObj, userId);
                AlertHandler(response, enqueueSnackbar, t);
                return response;
            }
        }, 200),
        []
    );

    useEffect(() => {
        if (user && userRoles) {
            setUserData(user);
            setIsLoading(false);
            setResponsibility(translate(responsibilities, t) as Option[]);
            setDepartment(translate(departments, t) as Option[]);
            setWorkFunction(
                userRoles.map((role) => ({
                    value: role.label,
                    label: role[i18n.language],
                })) as Option[]
            );
        }
    }, [user, userRoles]);

    useEffect(() => {
        Head.setTitle(t('profileTitle'));
        if (appInsights) {
            appInsights.trackPageView({ name: 'userProfile' });
        }
    }, []);

    useEffect(() => {
        updateUserWithDebounce(changes, userData, user?.id);
    }, [changes, userData?.id, user?.id]);

    const userViewMode = userData?.default_view_mode
        ? selected
        : mode[1]?.value;

    return (
        <Box
            id="conent_wrapper"
            sx={{
                bgcolor: '#ffffff',
                height: '100%',
                p: 4,
                overflowY: 'scroll',
                width: '100%',
            }}
        >
            <Box
                className="form__section"
                component={'section'}
                sx={{ pb: 12 }}
            >
                <Box component={'header'} sx={{ pb: 4 }}>
                    <SectionTitle variant="h6">
                        {t('profile.personal')}
                    </SectionTitle>
                </Box>
                <Box id="avatar_initials">
                    <AvatarInitials
                        firstName={stripPrefix(userData?.first_name) || ''}
                        lastName={userData?.last_name || ''}
                    />
                </Box>
                <Box id="form_wrapper" sx={{ pt: 6, maxWidth: 736 }}>
                    <Grid
                        container
                        spacing={{ mobile: 2, tablet: 4, laptop: 4 }}
                    >
                        <Grid mobile={12} laptop={6}>
                            <FormField
                                label={t('profile.firstName')}
                                id="first_name"
                            >
                                {isLoading ? (
                                    <Skeleton
                                        variant="rectangular"
                                        height={40}
                                        data-testid="loading-indicator"
                                    />
                                ) : (
                                    <OutlinedInput
                                        id="first_name"
                                        disabled
                                        value={
                                            stripPrefix(userData?.first_name) ||
                                            ''
                                        }
                                        size="small"
                                        style={{
                                            fontWeight: 400,
                                            fontSize: '16px',
                                            letterSpacing: '0.15px',
                                            lineHeight: '24px',
                                            width: '100%',
                                        }}
                                        sx={{
                                            '& fieldset': {
                                                border: '1px dotted',
                                            },
                                        }}
                                    />
                                )}
                            </FormField>
                        </Grid>
                        <Grid mobile={12} laptop={6}>
                            <FormField
                                label={t('profile.lastName')}
                                id="last_name"
                            >
                                {isLoading ? (
                                    <Skeleton
                                        variant="rectangular"
                                        height={40}
                                    />
                                ) : (
                                    <OutlinedInput
                                        id="last_name"
                                        disabled
                                        value={userData?.last_name || ''}
                                        size="small"
                                        style={{
                                            fontWeight: 400,
                                            fontSize: '16px',
                                            letterSpacing: '0.15px',
                                            lineHeight: '24px',
                                            width: '100%',
                                        }}
                                        sx={{
                                            '& fieldset': {
                                                border: '1px dotted',
                                            },
                                        }}
                                    />
                                )}
                            </FormField>
                        </Grid>
                        <Grid mobile={12}>
                            <FormField label={t('profile.email')} id="email">
                                {isLoading ? (
                                    <Skeleton
                                        variant="rectangular"
                                        height={40}
                                    />
                                ) : (
                                    <OutlinedInput
                                        id="email"
                                        disabled
                                        value={userData?.email || ''}
                                        size="small"
                                        style={{
                                            fontWeight: 400,
                                            fontSize: '16px',
                                            letterSpacing: '0.15px',
                                            lineHeight: '24px',
                                            width: '100%',
                                        }}
                                        sx={{
                                            '& fieldset': {
                                                border: '1px dotted',
                                            },
                                        }}
                                    />
                                )}
                            </FormField>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
            <Box id="form__section" component={'section'} sx={{ pb: 12 }}>
                <Box component={'header'}>
                    <SectionTitle variant="h6">
                        {t('profile.work')}
                    </SectionTitle>
                </Box>
                <Box className="form_wrapper" sx={{ maxWidth: 872 }}>
                    <Grid
                        container
                        spacing={{ mobile: 2, tablet: 4, laptop: 4 }}
                    >
                        <Grid mobile={12} laptop={6}>
                            <FormField
                                label={t('profile.functions')}
                                id="function"
                            >
                                {isLoading ? (
                                    <Skeleton
                                        variant="rectangular"
                                        height={40}
                                    />
                                ) : (
                                    <CustomAutocomplete
                                        id="function"
                                        onChange={handleAutocompleteOnchange}
                                        optionList={workFunction.find(
                                            ({ value }) =>
                                                value ===
                                                userData?.profile?.details
                                                    ?.function[0]
                                        )}
                                        //@ts-ignore
                                        options={sortOptons(workFunction)}
                                        isOptionEqualToValue={(option, value) =>
                                            option.value === value.value
                                        }
                                        placeholder={t(
                                            'profile.functionPlaceholder'
                                        )}
                                    />
                                )}
                            </FormField>
                        </Grid>
                        <Grid mobile={12} laptop={6}>
                            <FormField
                                label={t('profile.department')}
                                id="department"
                            >
                                {isLoading ? (
                                    <Skeleton
                                        variant="rectangular"
                                        height={40}
                                    />
                                ) : (
                                    <CustomAutocomplete
                                        multiple={true}
                                        limitTags={2}
                                        onChange={handleAutocompleteOnchange}
                                        optionList={department
                                            .filter((item) =>
                                                userData?.profile?.details?.department.includes(
                                                    item.value
                                                )
                                            )
                                            .map((item) => item)}
                                        id="department"
                                        //@ts-ignore
                                        options={sortOptons(department)}
                                        isOptionEqualToValue={(option, value) =>
                                            option.value === value.value
                                        }
                                        disableCloseOnSelect
                                        placeholder={t(
                                            'profile.departmentPlaceholder'
                                        )}
                                    />
                                )}
                            </FormField>
                        </Grid>
                        <Grid mobile={12} laptop={6}>
                            <FormField
                                label={t('profile.responsibility')}
                                id="responsibilities"
                            >
                                {isLoading ? (
                                    <Skeleton
                                        variant="rectangular"
                                        height={40}
                                    />
                                ) : (
                                    <CustomAutocomplete
                                        multiple={true}
                                        limitTags={2}
                                        id="responsibilities"
                                        onChange={handleAutocompleteOnchange}
                                        optionList={responsibility
                                            .filter((item) =>
                                                userData?.profile?.details?.responsibilities.includes(
                                                    item.value
                                                )
                                            )
                                            .map((item) => item)}
                                        //@ts-ignore
                                        options={sortOptons(responsibility)}
                                        isOptionEqualToValue={(option, value) =>
                                            option.value === value.value
                                        }
                                        disableCloseOnSelect
                                        placeholder={t(
                                            'profile.responsibilityPlaceholder'
                                        )}
                                    />
                                )}
                            </FormField>
                        </Grid>
                        <Grid mobile={12} laptop={6}>
                            <FormField
                                label={t('profile.coworker')}
                                id="working_with"
                            >
                                {isLoading ? (
                                    <Skeleton
                                        variant="rectangular"
                                        height={40}
                                    />
                                ) : (
                                    <CustomAutocomplete
                                        multiple={true}
                                        id="working_with"
                                        limitTags={2}
                                        onChange={handleAutocompleteOnchange}
                                        optionList={workFunction
                                            .filter((item) =>
                                                userData?.profile?.details?.working_with.includes(
                                                    item.value
                                                )
                                            )
                                            .map((item) => item)}
                                        //@ts-ignore
                                        options={sortOptons(workFunction)}
                                        disableCloseOnSelect
                                        isOptionEqualToValue={(option, value) =>
                                            option.value === value.value
                                        }
                                        placeholder={t(
                                            'profile.coworkerPlaceholder'
                                        )}
                                    />
                                )}
                            </FormField>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
            <Box id="form__section" component={'section'} sx={{ pb: 12 }}>
                <Box component={'header'} sx={{ pb: 4 }}>
                    <SectionTitle variant="h6">
                        {t('profile.preference')}
                    </SectionTitle>
                </Box>
                <Box className="form_wrapper" sx={{ maxWidth: 360 }}>
                    <Grid
                        container
                        spacing={{ mobile: 2, tablet: 4, laptop: 4 }}
                    >
                        <Grid mobile={12}>
                            <FormField
                                label={t('profile.defaultViewMode')}
                                id="view"
                            >
                                {isLoading ? (
                                    <Skeleton
                                        variant="rectangular"
                                        height={40}
                                    />
                                ) : (
                                    <Select
                                        data-testid="select-input"
                                        aria-label="Default mode to open views"
                                        id="view"
                                        value={userViewMode}
                                        onChange={handleSelectChange}
                                        sx={{
                                            width: '100%',
                                            '& .MuiSelect-select': {
                                                // Style the inner div here
                                                py: 1.75,
                                            },
                                        }}
                                    >
                                        {mode.map((role) => (
                                            <MenuItem
                                                key={role.value}
                                                value={role.value}
                                                data-testid={`select-option-${role.value}`}
                                            >
                                                {t(role.label)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            </FormField>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
            <Box className="form__section" component={'section'}>
                <Box component={'footer'}>
                    <FooterLink
                        to={{
                            pathname:
                                'https://cdn.bluedolphin.valueblue.nl/docs/PrivacyAndCookieStatement.pdf',
                        }}
                        target="_blank"
                    >
                        <Typography component={'h2'} variant="body2">
                            {t('profile.privacyText')}
                        </Typography>
                    </FooterLink>
                </Box>
            </Box>
        </Box>
    );
};
export default Profile;
